import {v4 as uuid} from 'uuid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { StrategyBuilderTab } from '../components/strategyBuilder/header'
import { StrategiesValue } from '../util/strategyBuilder'
import { Security } from '../proto/generated/security'
import { Broker_OrderType, Product, TradingCostItem, TransactionType } from '../proto/generated/order'
import { Condition } from '../proto/generated/nexus'
import { toDayStart } from '../util/dateTime';
import { PayoffLeg, PayoffProfitData, SdData } from '../components/payoffContainer/livePayoffContainer';
import { optionsTradesDataType, optionTradeDataType } from '../pages/typings/options.typings';
export interface StrategyBuilderStateType {
    mode: 'trade' | 'analyze' | 'squareOff',
    selectedTab: StrategyBuilderTab
    predefinedStrategy: StrategiesValue | undefined,
    tradingAccountId?: string,
    viewTabs: boolean,
    basketId?: string,
    batchId?: string,
    strategyGroup?: string,
    orderType: Broker_OrderType,
    product: Product,
    tradeOrders: TradeOrder[],
    waitingForOrders: boolean,
    loading: boolean,
    strategyInfo: StrategyInfo,
    error?: string,
    nearestExpiry?: number,
    targetValues: {
        spotBasePrice: number,
        ivMultiplier: number,
        spotTargetPrice: number,
        targetDate: number,
        priceThreshold: number
    },
    payoffLegs: PayoffLeg[],
    payoffLegsPositions: Record<string, number>
    payoffProfitData: PayoffProfitData[],
    targetPnl: number,
    sdData: SdData[],
    payoffStrikes: PayoffStrikeData[],
    optionsTradesData?: optionsTradesDataType,
    addOrderEnabled: boolean,
    exitStrategy: {
        addTarget: boolean,
        addStopLoss: boolean,
        targetValue: number,
        slValue: number
    },
    /** Conditional entry for basket trade panel (separate CE leg). */
    basketConditionalEntry: {
        enabled: boolean
        condition?: Condition
    },
    /** Conditional exit for basket trade panel (separate CX leg — square-off rule). */
    basketConditionalExit: {
        enabled: boolean
        condition?: Condition
    },
    limitOrderPremium: number,
    iceberg: {
        enabled: boolean,
        numberOfLegs: number
    }
}

export type StrategyInfo = {
    pop: number
    ext: number
    p50: number
    delta: number
    theta: number
    maxProfit: number
    maxLoss: number,
    totalMargin: number,
    brokerCharges: number,
    costBreakdown: TradingCostItem[],
    breakeven: number[],
    totalPremium: number,
}

export type TradeOrder = {
    security: Security,
    clientOrderId: string,
    transactionType: TransactionType,
    selected: boolean,
    referenceOrderId?: string,
    quantity: number,
    price?: number,
    linkedPosition?: string
}

export type PayoffStrikeData = {
    strike: number,
    callOi: number,
    putOi: number,
    callDelta: number,
    putDelta: number
}

export type PayoffData = {
    strikes: PayoffStrikeData[],
    profitData: PayoffProfitData[],
    optionsTradeData: optionTradeDataType,
    sdData: SdData[]
}

export const strategyBuilderInitialState: StrategyBuilderStateType = {
    mode: 'trade',
    selectedTab: StrategyBuilderTab.Payoff,
    predefinedStrategy: undefined,
    tradingAccountId: "",
    basketId: uuid(),
    batchId: "",
    strategyGroup: "",
    orderType: Broker_OrderType.MARKET_ORDER,
    product: Product.MARGIN,
    viewTabs: false,
    tradeOrders: [],
    waitingForOrders: false,
    loading: false,
    strategyInfo: {
        pop: 0,
        ext: 0,
        p50: 0,
        delta: 0,
        theta: 0,
        maxProfit: 0,
        maxLoss: 0,
        totalMargin: 0,
        brokerCharges: 0,
        costBreakdown: [],
        breakeven: [],
        totalPremium: 0
    },
    error: "",
    nearestExpiry: 0,
    targetValues: {
        ivMultiplier: 1,
        spotBasePrice: 0,
        spotTargetPrice: 0,
        targetDate: toDayStart(Date.now()).getTime(),
        priceThreshold: 0
    },
    targetPnl: 0,
    payoffLegs: [],
    payoffLegsPositions: {},
    payoffProfitData: [],
    sdData: [],
    payoffStrikes: [],
    addOrderEnabled: true,
    exitStrategy: {
        addTarget: false,
        addStopLoss: false,
        targetValue: 0,
        slValue: 0
    },
    basketConditionalEntry: {
        enabled: false,
        condition: undefined
    },
    basketConditionalExit: {
        enabled: false,
        condition: undefined
    },
    limitOrderPremium: 0,
    iceberg: {
        enabled: false,
        numberOfLegs: 1
    }
    
}

const strategyBuilderSlice = createSlice({
    name: 'strategyBuilder',
    initialState: strategyBuilderInitialState,
    reducers: {
        setSelectedTab(state, action) {
            state.selectedTab = action.payload
        },
        setStrategyBuilderMode(state, action: PayloadAction<StrategyBuilderStateType['mode']>) {
            return {
                ...state,
                mode: action.payload
            }
        },
        setPredefinedStrategy(state, action) {
            state.predefinedStrategy = action.payload
        },
        setViewTabs(state, action) {
            state.viewTabs = action.payload
        },
        updateTradeOrders(state, action: PayloadAction<TradeOrder[]>) {
            return {
                ...state,
                tradeOrders: action.payload
            }
        },
        removeTradeOrder(state, action: PayloadAction<string>) {
            return {
                ...state,
                tradeOrders: state.tradeOrders.filter(t => t.clientOrderId !== action.payload)
            }
        },
        updateTradeOrder(state, action: PayloadAction<{clientOrderId: string, update: Partial<TradeOrder>}>) {
            const { clientOrderId, update } = action.payload;
            return {
                ...state,
                tradeOrders: state.tradeOrders.map(t => {
                    if (t.clientOrderId === clientOrderId) {
                        return {
                            ...t,
                            ...update
                        }
                    }

                    return t
                })
            }
        },
        updateOrderType(state, action: PayloadAction<Broker_OrderType>) {
            return {
                ...state,
                orderType: action.payload
            }
        },
        updateTradeProduct(state, action: PayloadAction<Product>) {
            return {
                ...state,
                product: action.payload
            }
        },
        setLoading(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                loading: action.payload
            }
        },
        setWaitingForOrders(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                waitingForOrders: action.payload
            }
        },
        setError(state, action: PayloadAction<string>) {
            return {
                ...state,
                error: action.payload
            }
        },
        setStrategyGroup(state, action: PayloadAction<string>) {
            return {
                ...state,
                strategyGroup: action.payload
            }
        },
        setBasketId(state, action: PayloadAction<string>) {
            return {
                ...state,
                basketId: action.payload
            }
        },
        setBatchId(state, action: PayloadAction<string>) {
            return {
                ...state,
                batchId: action.payload
            }
        },
        updateStrategyInfo(state, action: PayloadAction<Partial<StrategyInfo>>) {
            return {
                ...state,
                strategyInfo: {
                    ...state.strategyInfo,
                    ...action.payload
                }
            }
        },
        resetStrategyBuilder() {
            return {
                ...strategyBuilderInitialState,
                basketId: uuid(),
            }
        },
        updateTargetValues(state, action: PayloadAction<Partial<StrategyBuilderStateType["targetValues"]>>) {
            return {
                ...state,
                 targetValues: {
                    ...state.targetValues,
                    ...action.payload
                }
            }
        },
        updateNearestExpiry(state, action: PayloadAction<number>) {
            return {
                ...state,
                nearestExpiry: action.payload
            }
        },
        setPayoffGraphData(state, action: PayloadAction<Partial<Pick<StrategyBuilderStateType, "payoffProfitData" | "payoffLegs" | "sdData" | "optionsTradesData" | "payoffStrikes" | "targetPnl">>>) {
            return {
                ...state,
                ...action.payload
            }
        },
        setPayoffProfitData(state, action: PayloadAction<PayoffProfitData[]>) {
            return {
                ...state,
                payoffProfitData: action.payload
            }
        },
        setSdData(state, action: PayloadAction<SdData[]>) {
            return {
                ...state,
                sdData: action.payload
            }
        },
        setPayoffLegsPositions(state, action: PayloadAction<Record<string, number>>) {
            return {
                ...state,
                payoffLegsPositions: action.payload
            }
        },
        setAddOrderEnabled(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                addOrderEnabled: action.payload
            }
        },
        updateExitStrategy(state, action: PayloadAction<Partial<StrategyBuilderStateType["exitStrategy"]>>) {
            return {
                ...state,
                exitStrategy: {
                    ...state.exitStrategy,
                    ...action.payload
                }
            }
        },
        updateBasketConditionalEntry(
            state,
            action: PayloadAction<Partial<StrategyBuilderStateType['basketConditionalEntry']>>
        ) {
            return {
                ...state,
                basketConditionalEntry: {
                    ...state.basketConditionalEntry,
                    ...action.payload
                }
            }
        },
        updateBasketConditionalExit(
            state,
            action: PayloadAction<Partial<StrategyBuilderStateType['basketConditionalExit']>>
        ) {
            return {
                ...state,
                basketConditionalExit: {
                    ...state.basketConditionalExit,
                    ...action.payload
                }
            }
        },
        updateLimitOrderPremium(state, action: PayloadAction<number>) {
            return {
                ...state,
                limitOrderPremium: action.payload
            }
        },
        updateIceberg(state, action: PayloadAction<Partial<StrategyBuilderStateType["iceberg"]>>) {
            const iceberg = { ...state.iceberg, ...action.payload }
            if (typeof iceberg.numberOfLegs === "number") {
                const n = Math.floor(iceberg.numberOfLegs);
                iceberg.numberOfLegs = Number.isFinite(n)
                    ? Math.max(1, Math.min(10, n))
                    : state.iceberg.numberOfLegs;
            }
            return {
                ...state,
                iceberg,
            }
        }
    }
})

const { actions, reducer: strategyBuilderReducer } = strategyBuilderSlice

export const { setSelectedTab, setStrategyBuilderMode, setPredefinedStrategy, setViewTabs,
        updateTradeOrders, removeTradeOrder, updateTradeOrder, updateOrderType, updateTradeProduct,
        setLoading, setWaitingForOrders, setError, setStrategyGroup, setBasketId, setBatchId,
        updateStrategyInfo, resetStrategyBuilder, updateTargetValues, updateNearestExpiry,
        setPayoffProfitData, setSdData, setPayoffGraphData, setPayoffLegsPositions, setAddOrderEnabled,
        updateExitStrategy, updateBasketConditionalEntry, updateBasketConditionalExit, updateLimitOrderPremium, updateIceberg } = actions


export default strategyBuilderReducer
